import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HistoryIcon, TriangleAlertIcon } from 'lucide-react';

import { ApiError, api, type ModuleChangePreview, type ModuleDetail, type ScoringConfig } from '@/lib/api';
import { useT, useTn } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ReadinessReport } from './readiness';

/**
 * The scoring-config editor, now with the two things `plan-beta` §6 asks for:
 * a **diff before saving** and a **version history**.
 *
 * WHY A PREVIEW STEP AND NOT JUST A SAVE. Editing a config is the one catalogue
 * act the cascade-killer does not make safe. A scoring-semantic edit mints a
 * version, and every in-flight run stays pinned to the one it launched with —
 * which is correct, and is also invisible. "Save" therefore asks the server what
 * it would do first, and the answer includes the fact nothing else can give
 * back: **how many runs are pinned to the version about to be superseded**.
 *
 * The editor itself is still the raw-JSON escape hatch `09 §5` allows alongside
 * structured editors. The structured half differs per model (RIASEC themes vs
 * Big Five traits vs skill ids) and is genuinely T5's remaining work; the
 * validation round-trip below is the load-bearing part and is real — the server
 * runs the module's own plugin, both on preview and on save.
 *
 * VERSION IS NOT EDITABLE. It is derived server-side and bumps only on a
 * scoring-semantic change; a renamed label must not bump it.
 */

function DiffRow({ entry }: { entry: ModuleChangePreview['configDiff'][number] }) {
  const t = useT();
  const render = (value: unknown) =>
    value === undefined ? t('config.absent') : JSON.stringify(value);

  return (
    <li className="space-y-1 border-s-2 ps-3 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-mono font-medium">{entry.field}</span>
        {entry.scoringSemantic ? (
          <Badge variant="secondary">{t('config.changesScores')}</Badge>
        ) : (
          <Badge variant="outline">{t('config.displayOnly')}</Badge>
        )}
      </div>
      <p className="text-muted-foreground truncate font-mono">− {render(entry.from)}</p>
      <p className="truncate font-mono">+ {render(entry.to)}</p>
    </li>
  );
}

function VersionHistory({ module }: { module: ModuleDetail }) {
  const t = useT();
  const history = module.scoringConfigHistory ?? [];

  if (history.length === 0) {
    return (
      <Alert>
        <HistoryIcon className="size-4" />
        <AlertDescription>
          {t('config.noHistory', { version: module.scoringConfig.version })}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">{t('config.history.hint')}</p>
      {[...history].reverse().map((config: ScoringConfig) => (
        <details key={config.version} className="rounded-md border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            v{config.version}
            <span className="text-muted-foreground ms-2 font-normal">
              {t('config.dimensionsBands', {
                dimensions: config.dimensions.length,
                bands: config.bands.length,
              })}
            </span>
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto text-xs">{JSON.stringify(config, null, 2)}</pre>
        </details>
      ))}
    </div>
  );
}

export function ConfigEditor({ moduleId, onClose }: { moduleId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const t = useT();
  const tn = useTn();
  const detail = useQuery({ queryKey: ['module', moduleId], queryFn: () => api.module(moduleId) });

  const [draft, setDraft] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ModuleChangePreview | null>(null);

  const text = draft ?? (detail.data ? JSON.stringify(detail.data.scoringConfig, null, 2) : '');

  /** Parse the editor, or report why it will not parse. Never throws. */
  function readDraft(): ScoringConfig | null {
    setParseError(null);
    try {
      return JSON.parse(text) as ScoringConfig;
    } catch (err) {
      setParseError(err instanceof Error ? err.message : t('config.notJson'));
      return null;
    }
  }

  /** The server returns field-level reasons from the plugin's own validateConfig. */
  function showFailure(err: unknown) {
    const details = err instanceof ApiError ? (err.payload.details as { errors?: string[] } | undefined) : undefined;
    setErrors(details?.errors ?? [err instanceof Error ? err.message : t('config.requestFailed')]);
  }

  const check = useMutation({
    mutationFn: (config: ScoringConfig) => api.previewModule(moduleId, { scoringConfig: config }),
    onSuccess: (report) => {
      setErrors([]);
      setPreview(report);
    },
    onError: (err) => {
      setPreview(null);
      showFailure(err);
    },
  });

  const save = useMutation({
    mutationFn: (config: ScoringConfig) => api.updateModule(moduleId, { scoringConfig: config }),
    onSuccess: (updated) => {
      setErrors([]);
      notify.success(
        t('config.saved'),
        t('config.saved.detail', { version: updated.scoringConfig.version }),
      );
      void queryClient.invalidateQueries({ queryKey: ['module', moduleId] });
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
      onClose();
    },
    onError: showFailure,
  });

  /**
   * Save is gated on a preview, and every keystroke clears the preview.
   *
   * Not ceremony: the preview is the only place the pinned-run count appears,
   * and a save that skipped it would be the version bump this dialog exists to
   * make visible, made invisible again by an impatient click. Clearing on edit
   * is what stops a preview of one config authorising the save of another.
   */
  return (
    <DialogContent className="max-w-3xl">
      <DialogHeader>
        <DialogTitle>{t('config.title', { module: moduleId })}</DialogTitle>
        <DialogDescription>{t('config.description')}</DialogDescription>
      </DialogHeader>

      {detail.isLoading && <Skeleton className="h-72 w-full" />}

      {detail.data && (
        <Tabs defaultValue="edit">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <TabsList>
              <TabsTrigger value="edit">{t('common.edit')}</TabsTrigger>
              <TabsTrigger value="history">
                {t('config.versionHistory')}
                {(detail.data.scoringConfigHistory?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="ms-2">
                    {detail.data.scoringConfigHistory.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">{detail.data.modelType}</Badge>
              <Badge variant="outline">
                {t('modules.configVersion', { version: detail.data.scoringConfig.version })}
              </Badge>
              <Badge variant="outline">
                {t('modules.pluginVersion', { version: detail.data.pluginVersion })}
              </Badge>
            </div>
          </div>

          <TabsContent value="edit" className="space-y-3">
            <Textarea
              value={text}
              onChange={(e) => {
                setDraft(e.target.value);
                // Any edit invalidates the preview it was not computed from.
                setPreview(null);
              }}
              spellCheck={false}
              className="h-64 font-mono text-xs"
              aria-label={t('config.jsonLabel')}
            />

            {parseError && (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{t('config.notJsonDetail', { error: parseError })}</AlertDescription>
              </Alert>
            )}

            {errors.length > 0 && (
              <Alert variant="destructive" role="alert">
                <AlertDescription>
                  <p className="mb-1 font-medium">{t('config.rejected')}</p>
                  <ul className="list-inside list-disc">
                    {errors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {preview && (
              <div className="space-y-3">
                {preview.configDiff.length === 0 ? (
                  <Alert>
                    <AlertDescription>{t('config.identical')}</AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <ul className="space-y-2">
                      {preview.configDiff.map((entry) => (
                        <DiffRow key={entry.field} entry={entry} />
                      ))}
                    </ul>

                    {preview.versionBump.bumps ? (
                      <Alert>
                        <TriangleAlertIcon className="size-4" />
                        <AlertDescription>
                          <p className="font-medium">
                            {t('config.versions', {
                              from: preview.versionBump.from,
                              to: preview.versionBump.to,
                            })}
                          </p>
                          <p className="text-muted-foreground mt-1 text-xs">
                            {tn('config.pinnedOpen', preview.pinnedToCurrent.open, {
                              version: preview.versionBump.from,
                            })}{' '}
                            {tn('config.pinnedCompleted', preview.pinnedToCurrent.completed)}
                          </p>
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert>
                        <AlertDescription>
                          {t('config.displayOnlyChange', { version: preview.versionBump.from })}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}

                <ReadinessReport readiness={preview.readiness} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <VersionHistory module={detail.data} />
          </TabsContent>
        </Tabs>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            const config = readDraft();
            if (config) check.mutate(config);
          }}
          disabled={check.isPending || !detail.data}
        >
          {check.isPending ? t('config.checking') : t('config.preview')}
        </Button>
        <Button
          onClick={() => {
            const config = readDraft();
            if (config) save.mutate(config);
          }}
          disabled={save.isPending || !detail.data || preview === null}
          title={preview === null ? t('config.previewFirst') : undefined}
        >
          {save.isPending ? t('common.saving') : t('config.save')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
